import { StyledTable } from '@/shared/ui/tables'
import React, { forwardRef } from 'react'
import {
    Alert,
    Box,
    ButtonGroup,
    IconButton,
    LinearProgress,
    Link as MuiLink,
    Paper,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronCircleLeft } from '@fortawesome/pro-duotone-svg-icons/faChevronCircleLeft'
import { faChevronCircleRight } from '@fortawesome/pro-duotone-svg-icons/faChevronCircleRight'
import { DossierData, TemplateOptions, CalendarEvent, Kedo } from '../types'
import {
    useCalendarData,
    formatDayName,
    formatDayNumber,
    formatMonthYear,
} from '../../../hooks'
import { LoadingState } from '../base'

interface EventCalendarBlockProps {
    kedo: Kedo
    data: DossierData | null
    templateOptions: TemplateOptions | null
    loading?: boolean
}

export const EventCalendarBlock = forwardRef(function EventCalendarBlock(
    { kedo, data, templateOptions, loading }: EventCalendarBlockProps,
    ref: React.ForwardedRef<HTMLDivElement>
) {
    const {
        events,
        locations,
        currentDay,
        nextDay,
        dayAfterNext,
        incrementDay,
        decrementDay,
        getEventsForLocation,
        isEventOnDay,
    } = useCalendarData({ data, templateOptions })

    if (
        !templateOptions ||
        templateOptions.name === null ||
        templateOptions.location === null ||
        templateOptions.start === null ||
        templateOptions.end === null
    ) {
        return (
            <Alert severity="warning">
                {kedo.t(
                    'Event module not properly setup. Please guide your contract admin.'
                )}
            </Alert>
        )
    }

    if (loading) {
        return <LoadingState />
    }

    const days = [currentDay, nextDay, dayAfterNext]

    const renderEventCell = (locationEvents: CalendarEvent[], day: Date) => {
        return (
            <TableCell key={day.toISOString()}>
                {locationEvents.map((event) => {
                    if (isEventOnDay(event, day, 'start')) {
                        return (
                            <MuiLink
                                key={`start-${event.id}`}
                                href={`/contentdossier/${event.id}`}
                                underline="none"
                            >
                                <Box sx={{ mb: 0.5 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={100}
                                        color="success"
                                        sx={{
                                            height: 24,
                                            borderRadius: 1,
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 1,
                                            },
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'relative',
                                            top: -20,
                                            left: 8,
                                            color: 'white',
                                        }}
                                    >
                                        {event.name}
                                    </Typography>
                                </Box>
                            </MuiLink>
                        )
                    }

                    if (isEventOnDay(event, day, 'end')) {
                        return (
                            <MuiLink
                                key={`end-${event.id}`}
                                href={`/contentdossier/${event.id}`}
                                underline="none"
                            >
                                <Box sx={{ mb: 0.5 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={100}
                                        color="error"
                                        sx={{
                                            height: 24,
                                            borderRadius: 1,
                                            '& .MuiLinearProgress-bar': {
                                                borderRadius: 1,
                                            },
                                        }}
                                    />
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'relative',
                                            top: -20,
                                            left: 8,
                                            color: 'white',
                                        }}
                                    >
                                        {event.name}
                                    </Typography>
                                </Box>
                            </MuiLink>
                        )
                    }

                    return null
                })}
            </TableCell>
        )
    }

    return (
        <Box ref={ref} id="eventcalendar">
            <TableContainer component={Paper}>
                <StyledTable size="small" striped>
                    <TableHead>
                        <TableRow
                            sx={{
                                '& .MuiTableCell-head': {
                                    textAlign: 'left',
                                    fontWeight: 'bold',
                                },
                            }}
                        >
                            <TableCell>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography variant="subtitle1">
                                        {formatMonthYear(currentDay)}
                                    </Typography>
                                    <ButtonGroup size="small">
                                        <IconButton
                                            onClick={decrementDay}
                                            size="small"
                                        >
                                            <FontAwesomeIcon
                                                icon={faChevronCircleLeft}
                                            />
                                        </IconButton>
                                        <IconButton
                                            onClick={incrementDay}
                                            size="small"
                                        >
                                            <FontAwesomeIcon
                                                icon={faChevronCircleRight}
                                            />
                                        </IconButton>
                                    </ButtonGroup>
                                </Box>
                            </TableCell>
                            {days.map((day) => (
                                <TableCell
                                    key={day.toISOString()}
                                    align="center"
                                >
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {formatDayName(day)}
                                    </Typography>
                                    <Typography variant="h6">
                                        {formatDayNumber(day)}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody sx={{}}>
                        {locations.map((location) => {
                            const locationEvents = getEventsForLocation(
                                location.name
                            )

                            return (
                                <TableRow
                                    key={`location-${
                                        location.id ?? location.name
                                    }`}
                                >
                                    <TableCell>
                                        <MuiLink
                                            href={`/contentdossier/${location.id}`}
                                        >
                                            {location.name}
                                        </MuiLink>
                                    </TableCell>
                                    {days.map((day) =>
                                        renderEventCell(locationEvents, day)
                                    )}
                                </TableRow>
                            )
                        })}
                    </TableBody>
                </StyledTable>
            </TableContainer>
        </Box>
    )
})

EventCalendarBlock.displayName = 'EventCalendarBlock'
